import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Globe, 
  Sparkles,
  FileText,
  PanelTop,
  MessageCircle,
  MessageSquareText,
  Users,
  UserCog,
  Menu,
  X,
  LogOut,
  Shield,
  Search,
  BookOpen,
  ChevronDown,
  Settings,
  Megaphone,
  BarChart3,
  Package,
  Type,
  Image,
  TrendingUp,
  Target,
  Wrench,
  Wand2,
  Activity,
  ShieldAlert,
  Link2,
  Bot
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

// Admin Cleanup 0 — projektägd asset, aldrig extern/preview-URL.
const ELIDO_LOGO_URL = "/elido-logo.png";

// Admin Cleanup 0 — exakt fem huvudområden. Tekniska verktyg bor under
// System → Tekniskt läge och ligger inte i den normala arbetsnavigationen.
const getNavCategories = (isAdmin) => {
  return [
    {
      id: "main",
      label: null, // Ingen kategori-rubrik för huvudsektionen
      items: [
        { path: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true, testid: "nav-dashboard" }
      ]
    },
    {
      id: "clients",
      label: "Kunder",
      icon: Users,
      items: [
        { path: "/admin/clients", label: "Kunder", icon: Users, testid: "nav-kunder" },
        { path: "/admin/onboarding-drafts", label: "Onboarding", icon: Sparkles, testid: "nav-onboarding" },
        { path: "/admin/google-kopplingar", label: "Google-kopplingar", icon: Link2, adminOnly: true, testid: "nav-google-kopplingar" }
      ]
    },
    {
      id: "sales",
      label: "Försäljning",
      icon: BarChart3,
      items: [
        { path: "/admin/offers", label: "Offerter", icon: FileText, testid: "nav-offerter" },
        { path: "/admin/demos", label: "Demos", icon: Globe },
        { path: "/admin/leads", label: "Säljleads", icon: Users, testid: "nav-elido-sales-leads" }
      ]
    },
    {
      id: "website",
      label: "Elidos webbplats",
      icon: PanelTop,
      adminOnly: true,
      items: [
        { path: "/admin/website", label: "Innehåll", icon: PanelTop },
        { path: "/admin/homepage-images", label: "Startsidans bilder", icon: Image },
        { path: "/admin/seo-pages", label: "SEO-sidor", icon: Search },
        { path: "/admin/blog", label: "Blogg", icon: BookOpen },
        { path: "/admin/media", label: "Mediabibliotek", icon: Image }
      ]
    },
    {
      id: "system",
      label: "System",
      icon: Settings,
      adminOnly: true,
      items: [
        { path: "/admin/users", label: "Användare", icon: UserCog, testid: "nav-anvandare" },
        { path: "/admin/chatgpt", label: "ChatGPT (Elido Intelligence)", icon: Bot, adminOnly: true, testid: "nav-chatgpt" },
        { path: "/admin/system/teknik", label: "Tekniskt läge", icon: Wrench, testid: "nav-tekniskt-lage" }
      ]
    }
  ];
};

// Platt lista för mobil och enklare rendering
const getFlatNavItems = (isAdmin) => {
  const categories = getNavCategories(isAdmin);
  const items = [];
  
  categories.forEach(cat => {
    if (cat.adminOnly && !isAdmin) return;
    cat.items.forEach(item => {
      if (item.adminOnly && !isAdmin) return;
      items.push({ ...item, category: cat.label });
    });
  });
  
  return items;
};

export default function AdminLayout({ children }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);
  const { user, logout, isAdmin } = useAuth();

  const categories = getNavCategories(isAdmin());
  const flatItems = getFlatNavItems(isAdmin());

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  const isCategoryActive = (category) => {
    return category.items.some(item => isActive(item));
  };

  // Filter categories based on role
  const visibleCategories = categories.filter(cat => {
    if (cat.adminOnly && !isAdmin()) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/admin" className="flex-shrink-0">
              <img src={ELIDO_LOGO_URL} alt="Elido" className="h-9" />
            </Link>

            {/* Desktop Navigation - Kategoriserad */}
            <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center mx-4">
              {visibleCategories.map((category) => {
                if (category.label === null) {
                  // Render items without dropdown for main category
                  return category.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        data-testid={item.testid}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                          isActive(item)
                            ? "bg-[#255E69]/10 text-[#255E69]"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  });
                }

                // Dropdown för kategorier
                const CategoryIcon = category.icon;
                const isOpen = openCategory === category.id;
                const hasActiveItem = isCategoryActive(category);

                return (
                  <div key={category.id} className="relative" data-testid={`nav-category-${category.id}`}>
                    <button
                      onClick={() => setOpenCategory(isOpen ? null : category.id)}
                      onBlur={() => setTimeout(() => setOpenCategory(null), 200)}
                      data-testid={`nav-category-trigger-${category.id}`}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                        hasActiveItem || isOpen
                          ? "bg-[#255E69]/10 text-[#255E69]"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      <CategoryIcon className="w-4 h-4" />
                      <span>{category.label}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>

                    {/* Dropdown menu */}
                    {isOpen && (
                      <div
                        className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                        data-testid={`nav-dropdown-${category.id}`}
                      >
                        {category.items.map((item) => {
                          const Icon = item.icon;
                          if (item.adminOnly && !isAdmin()) return null;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              data-testid={item.testid}
                              className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                                isActive(item)
                                  ? "bg-[#255E69]/10 text-[#255E69]"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              <Icon className="w-4 h-4" />
                              {item.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {user && (
                <div className="hidden md:flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#255E69] rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div className="hidden lg:flex flex-col">
                    <span className="text-sm font-medium text-gray-700 truncate max-w-[100px]">{user.name}</span>
                    <span className="text-xs text-gray-500">
                      {user.role === "admin" ? "Admin" : "Säljare"}
                    </span>
                  </div>
                </div>
              )}
              <button
                onClick={logout}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Logga ut"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logga ut</span>
              </button>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation - Kategoriserad */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white max-h-[70vh] overflow-y-auto">
            <nav className="px-4 py-3 space-y-4">
              {visibleCategories.map((category) => (
                <div key={category.id}>
                  {category.label && (
                    <div className="px-2 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      {category.label}
                    </div>
                  )}
                  <div className="space-y-1">
                    {category.items.map((item) => {
                      const Icon = item.icon;
                      if (item.adminOnly && !isAdmin()) return null;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                            isActive(item)
                              ? "bg-[#255E69]/10 text-[#255E69]"
                              : "text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* Mobile user info */}
            {user && (
              <div className="px-4 py-3 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#255E69] rounded-full flex items-center justify-center">
                    <span className="text-white font-medium">
                      {user.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{user.name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                    <div className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                      {user.role === "admin" ? (
                        <><Shield className="w-3 h-3" /> Administratör</>
                      ) : (
                        "Säljare"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}
